import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Compass, Globe2, RefreshCcw, Sparkles, Clock, MapPin, ShieldCheck } from "lucide-react";
import Marquee from "@/components/Marquee";
import Testimonials from "@/components/Testimonials";
import InquiryForm from "@/components/InquiryForm";
import { Reveal } from "@/components/Reveal";
import { SITE, scrollToId } from "@/lib/site";

const HERO_IMG = "/one-on-one-counselling.png";
const GALLERY = [
  {
    url: "/group-tuitions-classroom.jpg",
    caption: "Group Batches & Peer Learning",
    tag: "Group Tuitions",
  },
  {
    url: "https://images.unsplash.com/photo-1514369118554-e20d93546b30?crop=entropy&cs=srgb&fm=jpg&q=85",
    caption: "Personal Tuition & Structured Notes",
    tag: "Personal Tuitions",
  },
  {
    url: "https://images.unsplash.com/photo-1604177091072-b7b677a077f6?crop=entropy&cs=srgb&fm=jpg&q=85",
    caption: "Remote Digital Classrooms",
    tag: "Remote Areas",
  },
];

const FEATURES = [
  {
    icon: RefreshCcw,
    tag: "Student Comfort",
    title: "Easy Teacher Replacement",
    desc: "Not connecting with the tutor? We arrange a replacement without friction — no awkward conversations, no questions asked.",
  },
  {
    icon: Sparkles,
    tag: "Tailored Pedagogy",
    title: "Experienced & Young Faculty",
    desc: "The best blend of veteran academic mentors and dynamic young educators who relate to current exam trends.",
  },
  {
    icon: Globe2,
    tag: "Universal Access",
    title: "Practical Learning",
    desc: "Application-first syllabus delivery, visual problem solving and real-world linkages — far beyond rote memorization.",
  },
  {
    icon: Compass,
    tag: "Holistic Mentorship",
    title: "1-on-1 Student Counselling",
    desc: "Personalized career strategy, exam-anxiety support, subject streaming advice and regular parent-tutor reviews for future guidance.",
  },
];

const BOARD_CHIPS = ["CBSE", "ICSE / ISC", "State Boards", "Cambridge IGCSE", "IB"];

const heroLines = [
  <>Every Standard.</>,
  <>Every Board.</>,
  <>
    Every <em className="italic text-gold">Corner</em> of India.
  </>,
];

export default function Home() {
  const parallaxRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: parallaxRef,
    offset: ["start end", "end start"],
  });
  const parallaxY = useTransform(scrollYProgress, [0, 1], [50, -50]);

  return (
    <main data-testid="home-page">
      {/* ---------- HERO ---------- */}
      <section className="bg-dot-grid grain relative overflow-hidden pb-16 pt-28 sm:pt-32 lg:min-h-screen lg:pb-24 lg:pt-36">
        <div className="pointer-events-none absolute -left-40 top-24 h-[480px] w-[480px] rounded-full bg-[#EEF4FF] blur-3xl" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <motion.span
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="inline-flex items-center gap-2 rounded-full border border-[#D0E0FF] bg-[#EEF4FF] px-3.5 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-navy sm:text-xs"
            >
              <ShieldCheck className="h-3.5 w-3.5" />
              Estd. {SITE.founded} — {SITE.tagline}
            </motion.span>

            <h1 className="mt-7 font-serif text-4xl font-extrabold leading-[1.06] tracking-tight text-slate-900 sm:text-5xl lg:max-w-3xl lg:text-6xl xl:text-[4.4rem]">
              {heroLines.map((line, i) => (
                <span key={i} className="block overflow-hidden pb-1">
                  <motion.span
                    className="block"
                    initial={{ y: "112%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 1, delay: 0.15 + i * 0.14, ease: [0.16, 1, 0.3, 1] }}
                  >
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.65 }}
              className="mt-6 max-w-xl text-sm leading-relaxed text-slate-600 sm:text-base lg:text-lg"
            >
              Since {SITE.founded}, M² Tuitions has connected dedicated learners
              with top-tier educators — coaching for{" "}
              <span className="font-semibold text-navy-dark">
                all standards and all boards, throughout India's remote areas
              </span>
              . Two free demo lectures. One-on-one counselling. Zero guesswork.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <button
                data-testid="hero-book-demo-btn"
                onClick={() => scrollToId("inquiry")}
                className="group inline-flex items-center gap-2 rounded-full bg-navy px-7 py-3.5 text-sm font-bold text-white shadow-lg transition-all hover:-translate-y-0.5 hover:bg-navy-dark hover:shadow-xl"
              >
                Book 2 Free Demo Lectures
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
              <Link
                to="/careers"
                data-testid="hero-teachers-link"
                className="inline-flex items-center gap-2 rounded-full border border-navy/25 bg-white/70 px-7 py-3.5 text-sm font-bold text-navy-dark backdrop-blur transition-all hover:-translate-y-0.5 hover:border-navy hover:shadow-lg"
              >
                For Teachers — Teach & Earn
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.9, delay: 1 }}
              className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs font-semibold text-slate-500"
            >
              <span className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-gold" /> 2 Free Demos — 1 hr each
              </span>
              <span className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-gold" /> Remote-area ready
              </span>
              <span className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-gold" /> Trusted since {SITE.founded}
              </span>
            </motion.div>
          </div>

          {/* Parallax portrait */}
          <div className="lg:col-span-5" ref={parallaxRef}>
            <motion.div
              style={{ y: parallaxY }}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="relative mx-auto max-w-md"
            >
              <div className="absolute -right-5 -top-5 h-full w-full rounded-[2.2rem] border border-navy/20" />
              <img
                src={HERO_IMG}
                alt="1-on-1 counselling and doubt solving session"
                className="relative aspect-[4/5] w-full rounded-[2.2rem] object-cover shadow-[0_40px_80px_-30px_rgba(15,35,68,0.45)]"
              />
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -left-4 top-8 flex items-center gap-2 rounded-2xl border border-slate-100 bg-white/95 px-4 py-3 shadow-xl backdrop-blur sm:-left-8"
              >
                <Compass className="h-4 w-4 text-gold" />
                <span className="text-xs font-bold text-navy-dark">1-on-1 Counselling</span>
              </motion.div>
              <motion.div
                animate={{ y: [0, 8, 0] }}
                transition={{ duration: 4.6, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                className="absolute -bottom-5 right-4 flex items-center gap-2 rounded-2xl border border-slate-100 bg-white/95 px-4 py-3 shadow-xl backdrop-blur"
              >
                <RefreshCcw className="h-4 w-4 text-teal-600" />
                <span className="text-xs font-bold text-navy-dark">Easy Teacher Replacement</span>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      <Marquee />

      {/* ---------- FEATURES BENTO ---------- */}
      <section id="features" className="py-20 sm:py-24 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="max-w-2xl">
            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-navy">
              Why Families Choose M²
            </span>
            <h2 className="mt-4 font-serif text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
              Coaching built around your child — not the other way around.
            </h2>
          </Reveal>

          <div className="mt-12 grid gap-5 md:grid-cols-3 md:auto-rows-[minmax(190px,auto)]">
            <Reveal className="md:col-span-2 md:row-span-2">
              <div className="group flex h-full flex-col justify-between overflow-hidden rounded-3xl border border-slate-200 bg-navy-dark p-8 text-white shadow-sm transition-all hover:shadow-2xl sm:p-10">
                <div className="flex items-start justify-between gap-6">
                  <div>
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">
                      Zero Commitment
                    </span>
                    <h3 className="mt-3 font-serif text-2xl font-bold sm:text-3xl">
                      2 Free Demo Lectures
                    </h3>
                  </div>
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white/10">
                    <Clock className="h-6 w-6 text-gold" />
                  </span>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-white/70">
                  Experience our teaching methodology risk-free before
                  committing. Evaluate tutor chemistry and syllabus alignment —
                  one full hour each.
                </p>
                <div className="mt-8 grid gap-3 sm:grid-cols-2">
                  {["Demo Lecture 1 — 1 hr", "Demo Lecture 2 — 1 hr"].map((d, i) => (
                    <div
                      key={d}
                      className="flex items-center gap-3 rounded-2xl border border-white/15 bg-white/5 px-4 py-4"
                    >
                      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gold/20 font-serif text-sm font-bold text-gold">
                        {i + 1}
                      </span>
                      <span className="text-sm font-semibold">{d}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>

            {FEATURES.map((f, i) => (
              <Reveal key={f.title} delay={0.06 * (i + 1)}>
                <div className="group h-full rounded-3xl border border-slate-200 bg-white p-8 shadow-sm transition-all hover:-translate-y-1 hover:border-navy/25 hover:shadow-xl">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#EEF4FF] transition-colors group-hover:bg-navy">
                    <f.icon className="h-5 w-5 text-navy transition-colors group-hover:text-white" />
                  </span>
                  <span className="mt-6 block font-mono text-[10px] uppercase tracking-[0.2em] text-gold">
                    {f.tag}
                  </span>
                  <h3 className="mt-2 font-serif text-xl font-bold text-slate-900">
                    {f.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">
                    {f.desc}
                  </p>
                </div>
              </Reveal>
            ))}

            <Reveal delay={0.36} className="md:col-span-2">
              <div className="flex h-full flex-col justify-between gap-6 rounded-3xl border border-gold-border bg-gold-light p-8 sm:flex-row sm:items-center">
                <div>
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">
                    All Standards, Boards & Remote Areas
                  </span>
                  <h3 className="mt-2 font-serif text-xl font-bold text-slate-900 sm:text-2xl">
                    Class 1 to 12 + Foundation — every board, every mode.
                  </h3>
                </div>
                <button
                  data-testid="features-coverage-btn"
                  onClick={() => scrollToId("coverage")}
                  className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-navy px-5 py-2.5 text-sm font-bold text-white transition hover:bg-navy-dark"
                >
                  See Coverage
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </button>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ---------- COVERAGE ---------- */}
      <section id="coverage" className="grain relative overflow-hidden bg-navy-dark py-20 text-white sm:py-24 lg:py-28">
        <div className="pointer-events-none absolute -right-32 top-0 h-[420px] w-[420px] rounded-full bg-navy-light/40 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="max-w-3xl">
            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-gold">
              Pan-India Reach
            </span>
            <h2 className="mt-4 font-serif text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
              From metro cities to India's most remote districts.
            </h2>
            <p className="mt-5 text-sm leading-relaxed text-white/70 sm:text-base">
              Our tutors reach students wherever they are — at home, in small
              batches, or through live digital classrooms with zero-lag
              delivery for learners far from big-city coaching hubs.
            </p>
          </Reveal>

          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {[
              { t: "Personal Tuitions", d: "Dedicated 1-on-1 attention with a tutor matched to the student's pace and personality." },
              { t: "Group Tuitions", d: "Small, focused batches where students learn together and stay consistently motivated." },
              { t: "Home Tuitions", d: "Verified tutors who visit your home — safe, convenient and fully supervised." },
            ].map((m, i) => (
              <Reveal key={m.t} delay={0.08 * i}>
                <div className="h-full rounded-3xl border border-white/15 bg-white/5 p-8 backdrop-blur transition-all hover:-translate-y-1 hover:bg-white/10">
                  <span className="font-serif text-3xl font-extrabold text-gold">0{i + 1}</span>
                  <h3 className="mt-4 font-serif text-xl font-bold">{m.t}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-white/70">{m.d}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.2} className="mt-12 flex flex-wrap items-center gap-3">
            <span className="mr-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">
              Boards Covered
            </span>
            {BOARD_CHIPS.map((b) => (
              <span
                key={b}
                className="rounded-full border border-white/20 px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.14em] text-white/85"
              >
                {b}
              </span>
            ))}
          </Reveal>
        </div>
      </section>

      {/* ---------- GALLERY ---------- */}
      <section id="gallery" className="py-20 sm:py-24 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div className="max-w-xl">
              <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-navy">
                Student Life
              </span>
              <h2 className="mt-4 font-serif text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
                Inside our classrooms.
              </h2>
            </div>
            <p className="max-w-sm text-sm leading-relaxed text-slate-500">
              Real learning moments from M² batches — on campus, at home and
              online across the country.
            </p>
          </Reveal>

          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {GALLERY.map((g, i) => (
              <Reveal key={g.caption} delay={0.08 * i}>
                <div className={`group relative overflow-hidden rounded-3xl shadow-sm transition-shadow hover:shadow-2xl ${i === 0 ? "md:aspect-[3/4]" : "md:aspect-[3/4]"}`}>
                  <img
                    src={g.url}
                    alt={g.caption}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy-dark/80 via-transparent to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-6">
                    <span className="rounded-full bg-white/15 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-white backdrop-blur">
                      {g.tag}
                    </span>
                    <p className="mt-3 font-serif text-lg font-bold text-white">
                      {g.caption}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- TESTIMONIALS ---------- */}
      <Testimonials />

      {/* ---------- INQUIRY ---------- */}
      <section id="inquiry" className="bg-dot-grid py-20 sm:py-24 lg:py-28">
        <div className="mx-auto grid max-w-7xl items-start gap-12 px-5 sm:px-8 lg:grid-cols-2">
          <Reveal>
            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-navy">
              Admissions Open
            </span>
            <h2 className="mt-4 font-serif text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
              Book your 2 free demo lectures today.
            </h2>
            <p className="mt-5 max-w-lg text-sm leading-relaxed text-slate-600 sm:text-base">
              Tell us your child's standard, board and goals. We match the right
              teacher and schedule two full 1-hour demo lectures — completely
              free, completely your decision.
            </p>

            <div className="mt-8 space-y-4">
              {[
                "We call back within 24 hours",
                "Tutor matched to your board & subjects",
                "No obligation until you're fully satisfied",
              ].map((p) => (
                <div key={p} className="flex items-center gap-3 text-sm font-semibold text-slate-700">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gold-light">
                    <span className="h-2 w-2 rounded-full bg-gold" />
                  </span>
                  {p}
                </div>
              ))}
            </div>

            <div className="mt-10 rounded-3xl border border-slate-200 bg-white p-7 shadow-sm">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
                Prefer to talk directly?
              </p>
              <a
                href={SITE.phoneHref}
                data-testid="inquiry-call-card-link"
                className="mt-3 block font-serif text-2xl font-bold text-navy-dark transition hover:text-gold sm:text-3xl"
              >
                {SITE.phoneDisplay}
              </a>
              <a
                href={SITE.emailHref}
                data-testid="inquiry-mail-card-link"
                className="mt-2 block break-all text-sm font-semibold text-slate-500 transition hover:text-navy"
              >
                {SITE.email}
              </a>
              <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-gold">
                Serving families since {SITE.founded}
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="rounded-3xl border border-slate-200 bg-white p-7 shadow-[0_24px_60px_-30px_rgba(15,35,68,0.25)] sm:p-9">
              <InquiryForm />
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
